import React from 'react'
import { Platform, View } from 'react-native'
import styled from 'styled-components/native'

import { CountryButton } from 'features/identityCheck/components/countryPicker/CountryButton'
import { formatCallingCode } from 'features/identityCheck/components/countryPicker/formatCallingCode'
import { Country } from 'features/identityCheck/components/countryPicker/types'
import { AccessibilityRole } from 'libs/accessibilityRole/accessibilityRole'
import { getComputedAccessibilityLabel } from 'shared/accessibility/helpers/getComputedAccessibilityLabel'
import { useMobileFontScaleToDisplay } from 'shared/accessibility/helpers/zoomHelpers'
import { WHITELISTED_COUNTRIES } from 'shared/countries/constants'
import { styledButton } from 'ui/components/buttons/styledButton'
import { AppModal } from 'ui/components/modals/AppModal'
import { useModal } from 'ui/components/modals/useModal'
import { Touchable } from 'ui/components/touchable/Touchable'
import { ViewGap } from 'ui/components/ViewGap/ViewGap'
import { ArrowDown as DefaultArrowDown } from 'ui/svg/icons/ArrowDown'
import { Close } from 'ui/svg/icons/Close'
import { Typo } from 'ui/theme'

interface Props {
  selectedCountry: Country
  onSelect: (country: Country) => void
}

export const CountryPicker: React.FC<Props> = ({ selectedCountry, onSelect }) => {
  const { visible, showModal, hideModal } = useModal(false)

  const callingCode = formatCallingCode(selectedCountry.callingCode)

  function onCountrySelect(country: Country) {
    onSelect(country)
    hideModal()
  }

  const accessibilityLabel = getComputedAccessibilityLabel(
    callingCode,
    'Ouvrir la modale de choix de l’indicatif téléphonique'
  )

  const isZoomed = useMobileFontScaleToDisplay({ default: false, at200PercentZoom: true })

  return (
    <React.Fragment>
      <StyledTouchable
        onPress={showModal}
        hoverUnderlineColor={null}
        accessibilityLabel={accessibilityLabel}>
        <CallingCodeText>{callingCode}</CallingCodeText>
        <StyledViewGap gap={2}>
          <ArrowDown />
          <VerticalSeparator />
        </StyledViewGap>
      </StyledTouchable>
      <AppModal
        title="Choix de l’indicatif téléphonique"
        visible={visible}
        isUpToStatusBar={isZoomed}
        rightIconAccessibilityLabel="Fermer la modale de choix de l’indicatif téléphonique"
        rightIcon={Close}
        onRightIconPress={hideModal}>
        {WHITELISTED_COUNTRIES.length > 0 ? (
          <View accessibilityRole={AccessibilityRole.RADIOGROUP}>
            {WHITELISTED_COUNTRIES.map((country) => (
              <CountryButton
                key={country.id}
                country={country}
                selectedCountry={selectedCountry}
                onCountrySelect={onCountrySelect}
              />
            ))}
          </View>
        ) : null}
      </AppModal>
    </React.Fragment>
  )
}

const StyledViewGap = styled(ViewGap)({
  flexDirection: 'row',
  alignItems: 'center',
})
const focusStyle =
  Platform.OS === 'web'
    ? {
        '&:focus': { outline: 'none' },
        '&:focus-visible': { outline: 'auto' },
      }
    : {}

const StyledTouchable = styledButton(Touchable)({
  flexDirection: 'row',
  alignItems: 'center',
  flexShrink: 0,
  ...focusStyle,
})

const VerticalSeparator = styled.View(({ theme }) => ({
  paddingVertical: theme.designSystem.size.spacing.m,
  borderRightWidth: theme.designSystem.size.spacing.xxs,
  borderRightColor: theme.designSystem.color.border.default,
}))

const CallingCodeText = styled(Typo.Body)(({ theme }) => ({
  marginLeft: -theme.designSystem.size.spacing.xs, // To compensate for the Flag component right margin
  marginRight: theme.designSystem.size.spacing.xs,
}))

const ArrowDown = styled(DefaultArrowDown).attrs(({ theme }) => ({
  size: theme.designSystem.size.icon.s,
}))``
